import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface MemoryGameComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

interface MemoryGameItem {
  front: string;
  back: string;
}

interface MemoryCard {
  id: string;
  emoji: string;
  name: string;
  isFlipped: boolean;
  isMatched: boolean;
}

export const MemoryGameComponent: React.FC<MemoryGameComponentProps> = ({ 
  resource, 
  component, 
  onAction 
}) => {
  const { t } = useTranslation();
  const [cards, setCards] = useState<MemoryCard[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<number>(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);

  // Initialize cards from resource configuration
  useEffect(() => {
    const items = resource.items || [
      { front: '🐔', back: 'poule' },
      { front: '🐑', back: 'mouton' },
      { front: '🐦', back: 'canari' },
      { front: '🥄', back: 'pilon' },
      { front: '💡', back: 'lampe' },
      { front: '🏠', back: 'case' },
      { front: '👒', back: 'chapeau' }
    ];

    // Create pairs of cards
    const gameCards: MemoryCard[] = [];
    items.forEach((item: MemoryGameItem, index: number) => {
      // Add emoji card
      gameCards.push({
        id: `emoji-${index}`,
        emoji: item.front,
        name: item.back,
        isFlipped: false,
        isMatched: false
      });
      // Add name card
      gameCards.push({
        id: `name-${index}`,
        emoji: item.front,
        name: item.back,
        isFlipped: false,
        isMatched: false
      });
    });

    // Shuffle cards
    const shuffledCards = gameCards.sort(() => Math.random() - 0.5);
    setCards(shuffledCards);
  }, [resource.items]);

  const handleCardClick = (index: number) => {
    if (gameCompleted || !gameStarted) return;
    
    const card = cards[index];
    if (card.isFlipped || card.isMatched) return;

    const newFlippedCards = [...flippedCards, index];
    setFlippedCards(newFlippedCards);

    // Update card state
    const newCards = [...cards];
    newCards[index].isFlipped = true;
    setCards(newCards);

    onAction?.('card_flipped', { 
      cardIndex: index, 
      cardName: card.name,
      cardEmoji: card.emoji 
    });

    // Check for match when 2 cards are flipped
    if (newFlippedCards.length === 2) {
      setTimeout(() => {
        checkForMatch(newFlippedCards);
      }, 1000);
    }
  };

  const checkForMatch = (flippedIndices: number[]) => {
    const [index1, index2] = flippedIndices;
    const card1 = cards[index1];
    const card2 = cards[index2];

    if (card1.name === card2.name) {
      // Match found
      const newCards = [...cards];
      newCards[index1].isMatched = true;
      newCards[index2].isMatched = true;
      setCards(newCards);
      setMatchedPairs(prev => prev + 1);

      onAction?.('match_found', { 
        cardName: card1.name,
        cardIndices: [index1, index2]
      });

      // Check if game is completed
      if (matchedPairs + 1 === cards.length / 2) {
        setGameCompleted(true);
        onAction?.('game_completed', { 
          totalPairs: cards.length / 2,
          timeElapsed: Date.now()
        });
      }
    } else {
      // No match, flip cards back
      setTimeout(() => {
        const newCards = [...cards];
        newCards[index1].isFlipped = false;
        newCards[index2].isFlipped = false;
        setCards(newCards);
      }, 1000);
    }

    setFlippedCards([]);
  };

  const startGame = () => {
    setGameStarted(true);
    setGameCompleted(false);
    setMatchedPairs(0);
    setFlippedCards([]);
    
    // Reset all cards
    const resetCards = cards.map(card => ({
      ...card,
      isFlipped: false,
      isMatched: false
    }));
    setCards(resetCards);

    onAction?.('game_started', { totalCards: cards.length });
  };

  const resetGame = () => {
    setGameStarted(false);
    setGameCompleted(false);
    setMatchedPairs(0);
    setFlippedCards([]);
    
    // Reset all cards
    const resetCards = cards.map(card => ({
      ...card,
      isFlipped: false,
      isMatched: false
    }));
    setCards(resetCards);

    onAction?.('game_reset', {});
  };

  return (
    <div className="bg-purple-50 rounded-lg p-4">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-purple-900 mb-2">
          {resource.title || t('memoryGame.memoryGame')}
        </h3>
        <p className="text-sm text-purple-700">
          {t('memoryGame.matchEmojiWithName')}
        </p>
      </div>

      {/* Game Controls */}
      <div className="flex justify-center gap-4 mb-4">
        {!gameStarted && (
          <button
            onClick={startGame}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            {t('memoryGame.startGame')}
          </button>
        )}
        
        {gameStarted && !gameCompleted && (
          <button
            onClick={resetGame}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('memoryGame.resetGame')}
          </button>
        )}
      </div>

      {/* Game Stats */}
      {gameStarted && (
        <div className="text-center mb-4">
          <div className="inline-flex gap-4 bg-white rounded-lg px-4 py-2 border border-purple-200">
            <span className="text-sm text-purple-700">
              {t('memoryGame.matchedPairs')}: {matchedPairs} / {cards.length / 2}
            </span>
            <span className="text-sm text-purple-700">
              {t('memoryGame.status')}: {gameCompleted ? t('memoryGame.completed') : t('memoryGame.inProgress')}
            </span>
          </div>
        </div>
      )}

      {/* Game Grid */}
      <div className="bg-white rounded-lg p-4 border-2 border-purple-300">
        <div className="grid grid-cols-4 gap-2">
          {cards.map((card, index) => (
            <button
              key={card.id}
              onClick={() => handleCardClick(index)}
              disabled={!gameStarted || card.isMatched}
              className={`aspect-square rounded-lg border-2 transition-all duration-300 ${
                card.isMatched
                  ? 'bg-green-100 border-green-500 opacity-50 cursor-not-allowed'
                  : card.isFlipped
                  ? 'bg-purple-100 border-purple-500'
                  : 'bg-gray-100 border-gray-300 hover:border-purple-400 hover:bg-purple-50'
              } ${!gameStarted ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-center justify-center h-full">
                {card.isFlipped || card.isMatched ? (
                  <div className="text-center">
                    <div className="text-2xl mb-1">{card.emoji}</div>
                    <div className="text-xs font-medium text-gray-700">{card.name}</div>
                  </div>
                ) : (
                  <div className="text-gray-400 text-2xl">?</div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Game Completion Message */}
      {gameCompleted && (
        <div className="mt-4 p-4 bg-green-100 rounded-lg text-center">
          <div className="text-2xl mb-2">🎉</div>
          <h4 className="font-semibold text-green-900 mb-1">
            {t('memoryGame.congratulations')}
          </h4>
          <p className="text-sm text-green-800">
            {t('memoryGame.gameCompletedMessage')}
          </p>
        </div>
      )}
    </div>
  );
};
